import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Matrix4, Plane, Ray, ShapeGeometry, Vector3 } from 'three';
import { clamp } from 'three/src/math/MathUtils.js';

import { context } from '@react-three/drei/web/pivotControls/context.js';
import { ThreeEvent, useThree } from '@react-three/fiber';

import { CircleShape } from './CircleShape.js';

const clickDir = new Vector3();
const intersectionDir = new Vector3();

const toDegrees = (radians: number) => (radians * 180) / Math.PI;
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const calculateAngle = (
  clickPoint: Vector3,
  intersectionPoint: Vector3,
  origin: Vector3,
  e1: Vector3,
  e2: Vector3
) => {
  clickDir.copy(clickPoint).sub(origin);
  intersectionDir.copy(intersectionPoint).sub(origin);
  const dote1e1 = e1.dot(e1);
  const dote2e2 = e2.dot(e2);
  const uClick = clickDir.dot(e1) / dote1e1;
  const vClick = clickDir.dot(e2) / dote2e2;
  const uIntersection = intersectionDir.dot(e1) / dote1e1;
  const vIntersection = intersectionDir.dot(e2) / dote2e2;
  const angleClick = Math.atan2(vClick, uClick);
  const angleIntersection = Math.atan2(vIntersection, uIntersection);
  return angleIntersection - angleClick;
};

const fmod = (num: number, denom: number) => {
  let k = Math.floor(num / denom);
  k = k < 0 ? k + 1 : k;

  return num - k * denom;
};

const minimizeAngle = (angle: number) => {
  let result = fmod(angle, 2 * Math.PI);

  if (Math.abs(result) < 1e-6) {
    return 0.0;
  }

  if (result < 0.0) {
    result += 2 * Math.PI;
  }

  return result;
};

const rotMatrix = new Matrix4();
const posNew = new Vector3();
const ray = new Ray();
const intersection = new Vector3();

export const AxisRotator: React.FC<{ dir1: Vector3; dir2: Vector3; axis: 0 | 1 | 2 }> = ({
  dir1,
  dir2,
  axis,
}) => {
  const {
    rotationLimits,
    depthTest,
    scale,
    lineWidth,
    fixed,
    axisColors,
    hoveredColor,
    opacity,
    onDragStart,
    onDrag,
    onDragEnd,
    userData,
  } = useContext(context);
  // @ts-expect-error new in @react-three/fiber@7.0.5
  const camControls = useThree((state) => state.controls) as { enabled: boolean };
  const events = useThree((state) => state.events);
  const raycaster = useThree((state) => state.raycaster);
  const objRef = useRef<Group>(null!);
  const angle0 = useRef<number>(0);
  const angle = useRef<number>(0);
  const isDragging = useRef(false);
  const clickInfo = useRef<{
    clickPoint: Vector3;
    origin: Vector3;
    e1: Vector3;
    e2: Vector3;
    normal: Vector3;
    plane: Plane;
  } | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const onPointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      const clickPoint = e.point.clone();
      const origin = new Vector3().setFromMatrixPosition(objRef.current.matrixWorld);
      const e1 = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 0).normalize();
      const e2 = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 1).normalize();
      const normal = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 2).normalize();
      const plane = new Plane().setFromNormalAndCoplanarPoint(normal, origin);
      clickInfo.current = { clickPoint, origin, e1, e2, normal, plane };
      onDragStart({ component: 'Rotator', axis, origin, directions: [e1, e2, normal] });
      if (camControls) {
        camControls.enabled = false;
      }

      // @ts-ignore
      e.target.setPointerCapture(e.pointerId);
      isDragging.current = true;
    },
    [camControls, onDragStart, axis]
  );

  const onPointerMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (!isDragging.current) {
        return;
      }
      e.stopPropagation();
      if (!isHovered) setIsHovered(true);
      if (clickInfo.current) {
        const { clickPoint, origin, e1, e2, normal, plane } = clickInfo.current;
        const [min, max] = rotationLimits?.[axis] || [undefined, undefined];

        ray.copy(raycaster.ray);
        ray.intersectPlane(plane, intersection);
        ray.direction.negate();
        ray.intersectPlane(plane, intersection);

        let deltaAngle = calculateAngle(clickPoint, intersection, origin, e1, e2);
        let degrees = toDegrees(deltaAngle);

        // @ts-ignore
        if (e.shiftKey) {
          degrees = Math.round(degrees / 15) * 15;
          deltaAngle = toRadians(degrees);
        }

        if (min !== undefined && max !== undefined && max - min < 2 * Math.PI) {
          deltaAngle = minimizeAngle(deltaAngle);
          deltaAngle = deltaAngle > Math.PI ? deltaAngle - 2 * Math.PI : deltaAngle;
          deltaAngle = clamp(deltaAngle, min - angle0.current, max - angle0.current);
          angle.current = angle0.current + deltaAngle;
        } else {
          angle.current = minimizeAngle(angle0.current + deltaAngle);
          angle.current = angle.current > Math.PI ? angle.current - 2 * Math.PI : angle.current;
        }

        rotMatrix.makeRotationAxis(normal, deltaAngle);
        posNew.copy(origin).applyMatrix4(rotMatrix).sub(origin).negate();
        rotMatrix.setPosition(posNew);
        onDrag(rotMatrix);
      }
    },
    [isHovered, rotationLimits, axis, raycaster.ray, onDrag]
  );

  const onPointerUp = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (!isDragging.current) {
        return;
      }
      e.stopPropagation();
      angle0.current = angle.current;
      clickInfo.current = null;
      onDragEnd();
      if (camControls) {
        camControls.enabled = true;
      }
      // @ts-ignore
      e.target.releasePointerCapture(e.pointerId);
      isDragging.current = false;
    },
    [camControls, onDragEnd]
  );

  const onPointerOut = useCallback((e: any) => {
    e.stopPropagation();
    setIsHovered(false);
  }, []);

  const matrixL = useMemo(() => {
    const dir1N = dir1.clone().normalize();
    const dir2N = dir2.clone().normalize();
    return new Matrix4().makeBasis(dir1N, dir2N, dir1N.clone().cross(dir2N));
  }, [dir1, dir2]);

  const r = fixed ? 0.65 : scale * 0.65;

  const arc = useMemo(() => {
    const segments = 32;
    const points: Vector3[] = [];
    for (let j = 0; j <= segments; j++) {
      const angle = (j * Math.PI * 2) / segments;
      points.push(new Vector3(Math.cos(angle) * r, Math.sin(angle) * r, 0));
    }
    return points;
  }, [r]);

  useEffect(() => {
    if (events.connected) {
      events.connected.addEventListener('pointermove', onPointerMove);
      events.connected.addEventListener('pointerup', onPointerUp);
      return () => {
        events.connected.removeEventListener('pointermove', onPointerMove);
        events.connected.removeEventListener('pointerup', onPointerUp);
      };
    }
  }, [events, onPointerMove, onPointerUp]);

  const discGeometry = useMemo(() => new ShapeGeometry(new CircleShape(0.7, 0.6)), []);
  const color = isHovered ? hoveredColor : '#969696';

  return (
    <group
      ref={objRef}
      matrix={matrixL}
      matrixAutoUpdate={false}
      onPointerDown={onPointerDown}
      onPointerEnter={() => setIsHovered(true)}
      onPointerOut={onPointerOut}
    >
      <mesh scale={1.0} userData={userData}>
        <primitive object={discGeometry} />
        <meshBasicMaterial
          polygonOffset
          transparent
          color={color}
          depthTest={depthTest}
          fog={false}
          opacity={isHovered ? 1.0 : opacity}
          polygonOffsetFactor={-10}
        />
      </mesh>

      {/* The invisible mesh being raycast */}
      {/* <Line points={arc} lineWidth={lineWidth * 6} visible={false} userData={userData} /> */}
      {/* The visible mesh */}
      {/* <Line
        transparent
        raycast={() => null}
        depthTest={depthTest}
        points={arc}
        lineWidth={lineWidth}
        color={(isHovered ? hoveredColor : 'white') as any}
        opacity={isHovered ? 1.0 : opacity}
        polygonOffset
        polygonOffsetFactor={-10}
        fog={false}
      /> */}
    </group>
  );
};

export default AxisRotator;
